import React, { useState, useEffect } from 'react';
import { X, Smartphone, ArrowLeft, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PhoneOTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<boolean>;
  onBack?: () => void;
  phoneNumber?: string;
  loading?: boolean;
}

export default function PhoneOTPModal({
  isOpen,
  onClose,
  onVerify,
  onBack,
  phoneNumber = "+1 (***) ***-1234",
  loading = false
}: PhoneOTPModalProps) {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  if (!isOpen) return null;

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setIsVerifying(true);
    try {
      const success = await onVerify(verificationCode);
      if (!success) {
        setAttempts(prev => prev + 1);
        setVerificationCode('');
        
        if (attempts >= 2) {
          toast.error('Too many failed attempts. Please try again later.');
          onClose();
        }
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
      setVerificationCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    setResendCooldown(60);
    setAttempts(0);
    toast.success('SMS code sent to your phone');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verificationCode.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Phone Verification
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter the code sent to {phoneNumber}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <Smartphone className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>

          {/* Code Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Enter the 6-digit code from your SMS
            </p>
          </div>

          {/* Resend */}
          <div className="text-center mb-6">
            {resendCooldown > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resend code in {resendCooldown}s
              </p>
            ) : (
              <button
                onClick={handleResend}
                className="text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium flex items-center justify-center space-x-1"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Resend SMS Code</span>
              </button>
            )}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={verificationCode.length !== 6 || isVerifying || loading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
              verificationCode.length === 6 && !isVerifying && !loading
                ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isVerifying || loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Verifying...</span>
              </div>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Help Text */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Didn't receive the code? Check your phone and try again.
            </p>
          </div>

          {/* Attempts Warning */}
          {attempts > 0 && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
                {attempts === 1 ? '2 attempts remaining' : '1 attempt remaining'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
